import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "gsforge-theme";

export const themes = {
  dark: {
    bg: "#0B0D10",
    text: "#E9EBE6",
    border: "#20242A",
    component: {
      muted: "#9AA3AA",
      accent: "#34D6CE",
      accent2: "#5EE6DE",
      accent3: "#34D6CE",
      onAccent: "#071110",
      heroGradient: "none",
      heroSubtitle: "#E9EBE6",
      heroBody: "#9AA3AA",
      heroEmphasis: "#34D6CE",
      contactPanel: "transparent",
      inputBg: "#101317",
      inputText: "#E9EBE6",
      inputBorder: "#20242A",
      inputFocus: "#34D6CE",
      iconHoverBg: "rgba(255, 255, 255, 0.08)",
      iconHoverText: "#ffffff",
      soonBadgeBg: "#dc2626",
      soonBadgeText: "#ffffff",
    },
  },
  light: {
    bg: "#F5F6F3",
    text: "#14181F",
    border: "#DADDD6",
    component: {
      muted: "#586070",
      accent: "#0B6E69",
      accent2: "#128F88",
      accent3: "#0B6E69",
      onAccent: "#ffffff",
      heroGradient: "linear-gradient(90deg, #4b87db 0%, #8b6de0 45%, #1a9789 100%)",
      heroSubtitle: "#14181F",
      heroBody: "#586070",
      heroEmphasis: "#0B6E69",
      contactPanel: "#ffffff",
      inputBg: "#ffffff",
      inputText: "#14181F",
      inputBorder: "#DADDD6",
      inputFocus: "#0B6E69",
      iconHoverBg: "rgba(39, 48, 43, 0.08)",
      iconHoverText: "#27302b",
      soonBadgeBg: "#dc2626",// make it red and mode invariant
      soonBadgeText: "#ffffff",
    },
  },
};

const getInitialIsDark = () => {
  if (typeof window === "undefined") return true;

  const savedTheme = window.localStorage.getItem(STORAGE_KEY);
  if (savedTheme === "light") return false;
  if (savedTheme === "dark") return true;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(getInitialIsDark);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => !prev);
  }, []);

  useEffect(() => {
    const nextThemeName = isDark ? "dark" : "light";
    const body = document.body;

    body.classList.remove("dark", "light");
    body.classList.add(nextThemeName);

    document.documentElement.style.colorScheme = nextThemeName;
    window.localStorage.setItem(STORAGE_KEY, nextThemeName);
  }, [isDark]);

  const value = useMemo(
    () => ({
      isDark,
      theme: isDark ? themes.dark : themes.light,
      toggleTheme,
      themes,
    }),
    [isDark, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }

  return context;
};
